import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { API_BASE, useAuth } from './AuthContext';
import {
  CONFIRM_DELETE_STORAGE_KEY,
  DEFAULT_REDIRECT_STORAGE_KEY,
  TENANT_STORAGE_KEY,
  URL_CODE_REGEX,
  normalizeBaseUrl,
} from '../lib/admin-utils';
import i18n from '../i18n';
import type {
  AutoApproveDomainPattern,
  Analytics,
  InAppNotification,
  Notice,
  NotFoundPageConfig,
  PendingRegistration,
  PlatformAdmin,
  PlatformDomain,
  PlatformServiceAccount,
  RedirectType,
  ServiceAccountScope,
  TenantDomain,
  Tenant,
  TenantMember,
  TenantRole,
} from '../types/admin';

const t = (key: string) => i18n.t(key);

type MemberAssignableRole = 'admin' | 'editor' | 'viewer';

interface AdminPreferences {
  confirmDelete: boolean;
  defaultRedirectType: RedirectType;
}

interface CreateLinkInput {
  originalUrl: string;
  redirectType: RedirectType;
  urlCode?: string;
  domainId?: string;
  password?: string;
  redirectQueryParams?: Record<string, string>;
  activeFrom?: string;
  activeUntil?: string;
}

interface UpdateLinkInput {
  originalUrl: string;
  redirectType: RedirectType;
  password?: string;
  clearPassword?: boolean;
  redirectQueryParams?: Record<string, string>;
  clearRedirectQueryParams?: boolean;
  activeFrom?: string | null;
  activeUntil?: string | null;
}

interface AnalyticsQueryOptions {
  days: number;
  granularity: 'hour' | 'day';
  urlCode: string | null;
}

interface AdminContextValue {
  loading: boolean;
  notice: Notice;
  isPlatformAdmin: boolean;
  tenants: Tenant[];
  selectedTenantId: string | null;
  selectedTenant: Tenant | null;
  domains: TenantDomain[];
  notifications: InAppNotification[];
  unreadNotifications: number;
  notificationsLoading: boolean;
  analytics: Analytics | null;
  analyticsQuery: AnalyticsQueryOptions;
  members: TenantMember[];
  shortBaseUrl: string;
  canWriteLinks: boolean;
  canManageMembers: boolean;
  preferences: AdminPreferences;
  setNotice: (notice: Notice) => void;
  setSelectedTenantId: (tenantId: string | null) => void;
  refreshTenants: () => Promise<void>;
  refreshTenantData: () => Promise<void>;
  refreshAnalytics: (next?: Partial<AnalyticsQueryOptions>) => Promise<void>;
  refreshNotifications: () => Promise<void>;
  createTenant: (name: string) => Promise<void>;
  renameTenant: (tenantId: string, name: string) => Promise<void>;
  deleteTenant: (tenantId: string) => Promise<void>;
  createTenantDomain: (domain: string) => Promise<void>;
  refreshDomain: (domainId: string) => Promise<void>;
  setDefaultDomain: (domainId: string) => Promise<void>;
  deleteDomain: (domainId: string) => Promise<void>;
  addOrUpdateMember: (email: string, role: MemberAssignableRole) => Promise<void>;
  removeMember: (email: string) => Promise<void>;
  createLink: (input: CreateLinkInput) => Promise<{ urlCode: string; domainId?: string | null; domain?: string | null }>;
  updateLink: (urlCode: string, input: UpdateLinkInput, options?: { domainId?: string | null }) => Promise<void>;
  deleteLink: (urlCode: string, options?: { domainId?: string | null }) => Promise<void>;
  setDefaultRedirectType: (value: RedirectType) => void;
  setConfirmDelete: (value: boolean) => void;
  markNotificationRead: (notificationId: string) => Promise<void>;
  markAllNotificationsRead: () => Promise<void>;
  makeShortUrl: (code: string, domain?: string | null) => string;
  platformAdmins: PlatformAdmin[];
  platformAdminsLoading: boolean;
  fetchPlatformAdmins: () => Promise<void>;
  addPlatformAdmin: (email: string) => Promise<void>;
  removePlatformAdmin: (email: string) => Promise<void>;
  pendingRegistrations: PendingRegistration[];
  pendingRegistrationsLoading: boolean;
  fetchPendingRegistrations: () => Promise<void>;
  approveRegistration: (email: string) => Promise<void>;
  rejectRegistration: (email: string, reason?: string) => Promise<void>;
  autoApproveDomains: AutoApproveDomainPattern[];
  autoApproveDomainsLoading: boolean;
  fetchAutoApproveDomains: () => Promise<void>;
  addAutoApproveDomain: (pattern: string) => Promise<void>;
  removeAutoApproveDomain: (pattern: string) => Promise<void>;
  serviceAccounts: PlatformServiceAccount[];
  serviceAccountsLoading: boolean;
  fetchServiceAccounts: () => Promise<void>;
  createServiceAccount: (input: { name: string; actAsUserId: string; scopes: ServiceAccountScope[]; expiresAt?: string }) => Promise<{ apiKey: string; serviceAccount: PlatformServiceAccount }>;
  rotateServiceAccountKey: (serviceAccountId: string) => Promise<{ apiKey: string }>;
  revokeServiceAccount: (serviceAccountId: string) => Promise<void>;
  // Platform domain management
  platformDomains: PlatformDomain[];
  platformDomainsLoading: boolean;
  platformDefaultDomain: string;
  fetchPlatformDomains: () => Promise<void>;
  createPlatformDomain: (domain: string) => Promise<void>;
  linkDomainToAllWorkspaces: (domainId: string) => Promise<{ linked: number; defaultSet: number; skipped: number }>;
  linkDomainToWorkspace: (domainId: string, tenantId: string) => Promise<void>;
  unlinkDomainFromWorkspace: (domainId: string, tenantId: string) => Promise<void>;
  refreshPlatformDomain: (domainId: string) => Promise<void>;
  deletePlatformDomain: (domainId: string) => Promise<{ distributionId: string | null; note: string | null }>;
  getNotFoundPageConfig: (tenantId: string) => Promise<NotFoundPageConfig>;
  updateNotFoundPageConfig: (tenantId: string, config: Omit<NotFoundPageConfig, 'updatedAt' | 'updatedBy'>) => Promise<NotFoundPageConfig>;
}

const AdminContext = createContext<AdminContextValue | undefined>(undefined);
const NOTIFICATIONS_POLL_FAST_MS = 15_000;
const NOTIFICATIONS_POLL_MAX_MS = 120_000;

function isManageRole(role?: TenantRole): boolean {
  return role === 'owner' || role === 'admin';
}

function isWriteRole(role?: TenantRole): boolean {
  return role === 'owner' || role === 'admin' || role === 'editor';
}

async function parseApiError(response: Response, fallbackMessage: string): Promise<never> {
  const data = await response.json().catch(() => null);
  const message = typeof data?.error === 'string' ? data.error : fallbackMessage;
  throw new Error(message);
}

function roleForTenant(tenantList: Tenant[], tenantId: string): TenantRole | undefined {
  return tenantList.find((tenant) => tenant.tenantId === tenantId)?.role;
}

function normalizeAnalyticsQueryOptions(next?: Partial<AnalyticsQueryOptions>): AnalyticsQueryOptions {
  const rawDays = Number(next?.days);
  const days = Number.isFinite(rawDays) ? Math.min(90, Math.max(1, Math.floor(rawDays))) : 7;
  const granularity = next?.granularity === 'day' ? 'day' : 'hour';
  const urlCode = typeof next?.urlCode === 'string' ? next.urlCode.trim() || null : null;

  return {
    days,
    granularity,
    urlCode,
  };
}

function notificationEquals(left: InAppNotification, right: InAppNotification): boolean {
  return left.notificationId === right.notificationId
    && left.level === right.level
    && left.title === right.title
    && left.message === right.message
    && left.tenantId === right.tenantId
    && left.createdAt === right.createdAt
    && left.readAt === right.readAt
    && left.actionUrl === right.actionUrl
    && left.source === right.source;
}

// Reuse unchanged notification items so background polls do not repaint the panel.
function mergeNotifications(current: InAppNotification[], incoming: InAppNotification[]): InAppNotification[] {
  if (incoming.length === 0) {
    return current.length === 0 ? current : [];
  }

  const currentById = new Map(current.map((item) => [item.notificationId, item]));
  let changed = current.length !== incoming.length;

  const next = incoming.map((item, index) => {
    const existing = currentById.get(item.notificationId);
    const normalized = existing?.readAt && !item.readAt
      ? { ...item, readAt: existing.readAt }
      : item;

    if (current[index]?.notificationId !== normalized.notificationId) {
      changed = true;
    }

    if (existing && notificationEquals(existing, normalized)) {
      return existing;
    }

    changed = true;
    return normalized;
  });

  return changed ? next : current;
}

export function AdminProvider({ children }: { children: ReactNode }) {
  const { token } = useAuth();

  const [notice, setNotice] = useState<Notice>(null);
  const [isPlatformAdmin, setIsPlatformAdmin] = useState(false);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [selectedTenantId, setSelectedTenantId] = useState<string | null>(() => localStorage.getItem(TENANT_STORAGE_KEY));

  const [domains, setDomains] = useState<TenantDomain[]>([]);
  const [notifications, setNotifications] = useState<InAppNotification[]>([]);
  const [unreadNotifications, setUnreadNotifications] = useState(0);
  const [analytics, setAnalytics] = useState<Analytics | null>(null);
  const [analyticsQuery, setAnalyticsQuery] = useState<AnalyticsQueryOptions>(() => normalizeAnalyticsQueryOptions({
    days: 7,
    granularity: 'hour',
    urlCode: null,
  }));
  const [members, setMembers] = useState<TenantMember[]>([]);

  const [initializing, setInitializing] = useState(true);
  const [syncingTenant, setSyncingTenant] = useState(false);
  const [notificationsLoading, setNotificationsLoading] = useState(false);
  const [platformRedirectBaseUrl, setPlatformRedirectBaseUrl] = useState<string>('');
  const [platformAdmins, setPlatformAdmins] = useState<PlatformAdmin[]>([]);
  const [platformAdminsLoading, setPlatformAdminsLoading] = useState(false);
  const [pendingRegistrations, setPendingRegistrations] = useState<PendingRegistration[]>([]);
  const [pendingRegistrationsLoading, setPendingRegistrationsLoading] = useState(false);
  const [autoApproveDomains, setAutoApproveDomains] = useState<AutoApproveDomainPattern[]>([]);
  const [autoApproveDomainsLoading, setAutoApproveDomainsLoading] = useState(false);
  const [platformDomains, setPlatformDomains] = useState<PlatformDomain[]>([]);
  const [platformDomainsLoading, setPlatformDomainsLoading] = useState(false);
  const [serviceAccounts, setServiceAccounts] = useState<PlatformServiceAccount[]>([]);
  const [serviceAccountsLoading, setServiceAccountsLoading] = useState(false);
  const [platformDefaultDomain, setPlatformDefaultDomainState] = useState<string>('');
  const notificationsRequestIdRef = useRef(0);
  const notificationsLoadingRequestIdRef = useRef<number | null>(null);
  const notificationsTenantRef = useRef<string | null>(null);
  const notificationsSnapshotRef = useRef<InAppNotification[]>([]);
  const unreadNotificationsRef = useRef(0);

  const [defaultRedirectType, setDefaultRedirectTypeState] = useState<RedirectType>(() => {
    const stored = localStorage.getItem(DEFAULT_REDIRECT_STORAGE_KEY);
    return stored === '302' ? '302' : '301';
  });
  const [confirmDelete, setConfirmDeleteState] = useState<boolean>(() => {
    return localStorage.getItem(CONFIRM_DELETE_STORAGE_KEY) !== 'false';
  });

  const loading = initializing || syncingTenant;

  const authHeaders = useMemo(
    () => ({
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    }),
    [token],
  );

  const selectedTenant = useMemo(
    () => tenants.find((tenant) => tenant.tenantId === selectedTenantId) || null,
    [selectedTenantId, tenants],
  );

  const shortBaseUrl = useMemo(() => {
    const defaultActiveDomain = domains.find((item) => item.isDefault && item.status === 'active');
    if (defaultActiveDomain?.domain) {
      return `https://${defaultActiveDomain.domain}`;
    }

    const fallbackActiveDomain = domains.find((item) => item.status === 'active');
    if (fallbackActiveDomain?.domain) {
      return `https://${fallbackActiveDomain.domain}`;
    }

    return platformRedirectBaseUrl || '';
  }, [domains, platformRedirectBaseUrl]);

  const canManageMembers = isManageRole(selectedTenant?.role);
  const canWriteLinks = isWriteRole(selectedTenant?.role);

  useEffect(() => {
    if (!notice) {
      return;
    }

    const timeout = window.setTimeout(() => {
      setNotice(null);
    }, 3500);

    return () => window.clearTimeout(timeout);
  }, [notice]);

  const fetchTenants = useCallback(async (): Promise<Tenant[]> => {
    const response = await fetch(`${API_BASE}/tenants`, { headers: authHeaders });
    if (!response.ok) {
      return parseApiError(response, 'Failed to load tenants');
    }

    const data = await response.json();
    const list: Tenant[] = Array.isArray(data.tenants) ? data.tenants : [];
    setIsPlatformAdmin(Boolean(data.isPlatformAdmin));
    setTenants(list);
    return list;
  }, [authHeaders]);

  const fetchAnalytics = useCallback(async (tenantId: string, query: AnalyticsQueryOptions): Promise<void> => {
    const params = new URLSearchParams();
    params.set('tenantId', tenantId);
    params.set('days', String(query.days));
    params.set('granularity', query.granularity);
    if (query.urlCode) {
      params.set('urlCode', query.urlCode);
    }

    const response = await fetch(`${API_BASE}/analytics?${params.toString()}`, { headers: authHeaders });

    if (!response.ok) {
      setAnalytics(null);
      return parseApiError(response, 'Failed to load analytics');
    }

    const data: Analytics = await response.json();
    setAnalytics(data);
    setAnalyticsQuery(query);
  }, [authHeaders]);

  const fetchMembers = useCallback(async (tenantId: string, allowed: boolean): Promise<void> => {
    if (!allowed) {
      setMembers([]);
      return;
    }

    const response = await fetch(`${API_BASE}/tenants/${encodeURIComponent(tenantId)}/members`, { headers: authHeaders });
    if (!response.ok) {
      return parseApiError(response, 'Failed to fetch members');
    }

    const data = await response.json();
    const list: TenantMember[] = Array.isArray(data.members) ? data.members : [];
    setMembers(list);
  }, [authHeaders]);

  const fetchDomains = useCallback(async (tenantId: string): Promise<TenantDomain[]> => {
    const response = await fetch(`${API_BASE}/domains?tenantId=${encodeURIComponent(tenantId)}`, { headers: authHeaders });
    if (!response.ok) {
      return parseApiError(response, 'Failed to fetch domains');
    }

    const data = await response.json();
    const list: TenantDomain[] = Array.isArray(data.domains) ? data.domains : [];
    setDomains(list);

    return list;
  }, [authHeaders]);

  const fetchNotifications = useCallback(async (
    tenantId: string | null,
    options?: { silent?: boolean },
  ): Promise<{ changed: boolean }> => {
    const silent = Boolean(options?.silent);
    const requestId = notificationsRequestIdRef.current + 1;
    notificationsRequestIdRef.current = requestId;
    let changed = false;

    if (!silent) {
      notificationsLoadingRequestIdRef.current = requestId;
      setNotificationsLoading(true);
    }

    try {
      const query = new URLSearchParams();
      if (tenantId) {
        query.set('tenantId', tenantId);
      }
      query.set('limit', '60');

      const response = await fetch(`${API_BASE}/notifications?${query.toString()}`, { headers: authHeaders });
      if (requestId !== notificationsRequestIdRef.current) {
        return { changed: false };
      }

      if (!response.ok) {
        return parseApiError(response, 'Failed to fetch notifications');
      }

      const data = await response.json();
      const list: InAppNotification[] = Array.isArray(data.notifications) ? data.notifications : [];
      setNotifications((current) => {
        const merged = mergeNotifications(current, list);
        if (merged !== current) {
          changed = true;
        }
        notificationsSnapshotRef.current = merged;
        return merged;
      });
      setUnreadNotifications((current) => {
        const next = Number(data.unreadCount || 0);
        if (next !== current) {
          changed = true;
        }
        unreadNotificationsRef.current = next;
        return current === next ? current : next;
      });
    } finally {
      if (!silent && notificationsLoadingRequestIdRef.current === requestId) {
        notificationsLoadingRequestIdRef.current = null;
        setNotificationsLoading(false);
      }
    }
    return { changed };
  }, [authHeaders]);

  const refreshTenantDataFor = useCallback(async (tenantId: string, tenantList: Tenant[]): Promise<void> => {
    const role = roleForTenant(tenantList, tenantId);

    await Promise.all([
      fetchDomains(tenantId),
      fetchMembers(tenantId, isManageRole(role)),
    ]);
  }, [fetchDomains, fetchMembers]);

  const refreshTenants = useCallback(async () => {
    await fetchTenants();
  }, [fetchTenants]);

  const refreshTenantData = useCallback(async () => {
    if (!selectedTenantId) {
      return;
    }

    await refreshTenantDataFor(selectedTenantId, tenants);
  }, [refreshTenantDataFor, selectedTenantId, tenants]);

  const refreshAnalytics = useCallback(async (next?: Partial<AnalyticsQueryOptions>) => {
    if (!selectedTenantId) {
      return;
    }

    const normalized = normalizeAnalyticsQueryOptions({
      ...analyticsQuery,
      ...next,
    });
    await fetchAnalytics(selectedTenantId, normalized);
  }, [analyticsQuery, fetchAnalytics, selectedTenantId]);

  const refreshNotifications = useCallback(async () => {
    await fetchNotifications(selectedTenantId, { silent: notifications.length > 0 });
  }, [fetchNotifications, notifications.length, selectedTenantId]);

  useEffect(() => {
    notificationsSnapshotRef.current = notifications;
  }, [notifications]);

  useEffect(() => {
    unreadNotificationsRef.current = unreadNotifications;
  }, [unreadNotifications]);

  useEffect(() => {
    let active = true;

    const loadInitial = async () => {
      setInitializing(true);

      try {
        const tenantList = await fetchTenants();
        if (!active) {
          return;
        }

        const response = await fetch(`${API_BASE}/config`, { headers: authHeaders });
        if (response.ok) {
          const data = await response.json();
          const normalized = normalizeBaseUrl(String(data.redirectBaseUrl || ''));
          if (normalized) {
            setPlatformRedirectBaseUrl(normalized);
          }
          if (data.platformDefaultDomain) {
            setPlatformDefaultDomainState(String(data.platformDefaultDomain));
          }
        }

        if (tenantList.length === 0) {
          setSelectedTenantId(null);
          setDomains([]);
          setNotifications([]);
          setUnreadNotifications(0);
          setAnalytics(null);
          setMembers([]);
          return;
        }

        const storedTenantId = localStorage.getItem(TENANT_STORAGE_KEY);
        const initialTenantId = storedTenantId && tenantList.some((tenant) => tenant.tenantId === storedTenantId)
          ? storedTenantId
          : tenantList[0].tenantId;

        setSelectedTenantId(initialTenantId);
      } catch (error) {
        if (active) {
          const message = error instanceof Error ? error.message : t('ctx.errorInit');
          setNotice({ type: 'error', message });
        }
      } finally {
        if (active) {
          setInitializing(false);
        }
      }
    };

    loadInitial();

    return () => {
      active = false;
    };
  }, [authHeaders, fetchTenants]);

  useEffect(() => {
    if (tenants.length === 0) {
      localStorage.removeItem(TENANT_STORAGE_KEY);
      return;
    }

    if (!selectedTenantId || !tenants.some((tenant) => tenant.tenantId === selectedTenantId)) {
      setSelectedTenantId(tenants[0].tenantId);
    }
  }, [selectedTenantId, tenants]);

  useEffect(() => {
    if (!selectedTenantId) {
      setDomains([]);
      setNotifications([]);
      setUnreadNotifications(0);
      setAnalytics(null);
      setAnalyticsQuery(normalizeAnalyticsQueryOptions({
        days: 7,
        granularity: 'hour',
        urlCode: null,
      }));
      setMembers([]);
      return;
    }

    localStorage.setItem(TENANT_STORAGE_KEY, selectedTenantId);

    let active = true;

    const syncTenant = async () => {
      setSyncingTenant(true);
      setAnalytics(null);
      setAnalyticsQuery(normalizeAnalyticsQueryOptions({
        days: 7,
        granularity: 'hour',
        urlCode: null,
      }));

      try {
        await refreshTenantDataFor(selectedTenantId, tenants);
      } catch (error) {
        if (active) {
          const message = error instanceof Error ? error.message : t('ctx.errorRefreshWorkspace');
          setNotice({ type: 'error', message });
        }
      } finally {
        if (active) {
          setSyncingTenant(false);
        }
      }
    };

    syncTenant();

    return () => {
      active = false;
    };
  }, [refreshTenantDataFor, selectedTenantId, tenants]);

  useEffect(() => {
    if (initializing) {
      return;
    }

    if (selectedTenantId && !tenants.some((tenant) => tenant.tenantId === selectedTenantId)) {
      return;
    }

    let active = true;
    let timer: number | undefined;
    let pollDelayMs = NOTIFICATIONS_POLL_FAST_MS;

    const tenantChanged = notificationsTenantRef.current !== selectedTenantId;
    notificationsTenantRef.current = selectedTenantId;

    if (tenantChanged) {
      setNotifications([]);
      setUnreadNotifications(0);
      notificationsSnapshotRef.current = [];
      unreadNotificationsRef.current = 0;
    }

    const scheduleNextPoll = () => {
      if (!active || document.visibilityState !== 'visible') {
        return;
      }

      timer = window.setTimeout(() => {
        void syncNotifications({ silent: true });
      }, pollDelayMs);
    };

    const syncNotifications = async (options?: {
      reportErrors?: boolean;
      silent?: boolean;
      forceFastPoll?: boolean;
    }) => {
      if (document.visibilityState === 'hidden') {
        return;
      }

      try {
        const result = await fetchNotifications(selectedTenantId, { silent: options?.silent });
        if (options?.forceFastPoll || result.changed) {
          pollDelayMs = NOTIFICATIONS_POLL_FAST_MS;
        } else {
          pollDelayMs = Math.min(pollDelayMs * 2, NOTIFICATIONS_POLL_MAX_MS);
        }
      } catch (error) {
        pollDelayMs = Math.min(Math.max(pollDelayMs, NOTIFICATIONS_POLL_FAST_MS) * 2, NOTIFICATIONS_POLL_MAX_MS);
        if (active && options?.reportErrors) {
          const message = error instanceof Error ? error.message : t('ctx.errorRefreshNotifications');
          setNotice({ type: 'error', message });
        }
      } finally {
        scheduleNextPoll();
      }
    };

    void syncNotifications({
      reportErrors: tenantChanged,
      silent: !tenantChanged,
      forceFastPoll: true,
    });

    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible') {
        if (timer) {
          window.clearTimeout(timer);
          timer = undefined;
        }
        return;
      }

      pollDelayMs = NOTIFICATIONS_POLL_FAST_MS;
      void syncNotifications({ silent: true, forceFastPoll: true });
    };

    const handleWindowFocus = () => {
      if (document.visibilityState !== 'visible') {
        return;
      }

      pollDelayMs = NOTIFICATIONS_POLL_FAST_MS;
      void syncNotifications({ silent: true, forceFastPoll: true });
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleWindowFocus);

    return () => {
      active = false;
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleWindowFocus);
      if (timer) {
        window.clearTimeout(timer);
      }
    };
  }, [fetchNotifications, initializing, selectedTenantId, setNotice, tenants]);

  const createTenant = useCallback(async (name: string): Promise<void> => {
    const payload = name.trim();

    const response = await fetch(`${API_BASE}/tenants`, {
      method: 'POST',
      headers: authHeaders,
      body: JSON.stringify({ name: payload || undefined }),
    });

    if (!response.ok) {
      return parseApiError(response, 'Failed to create workspace');
    }

    const data = await response.json();
    const tenantList = await fetchTenants();

    const createdTenantId = typeof data.tenantId === 'string' ? data.tenantId : null;
    const nextTenantId = createdTenantId && tenantList.some((tenant) => tenant.tenantId === createdTenantId)
      ? createdTenantId
      : (tenantList[0]?.tenantId || null);

    if (nextTenantId) {
      setSelectedTenantId(nextTenantId);
    }
  }, [authHeaders, fetchTenants]);

  const renameTenant = useCallback(async (tenantId: string, name: string): Promise<void> => {
    const response = await fetch(`${API_BASE}/tenants/${encodeURIComponent(tenantId)}`, {
      method: 'PUT',
      headers: { ...authHeaders, 'Content-Type': 'application/json' },
      body: JSON.stringify({ name }),
    });
    if (!response.ok) {
      return parseApiError(response, 'Failed to rename workspace');
    }
    await fetchTenants();
  }, [authHeaders, fetchTenants]);

  const deleteTenant = useCallback(async (tenantId: string): Promise<void> => {
    const response = await fetch(`${API_BASE}/tenants/${encodeURIComponent(tenantId)}`, {
      method: 'DELETE',
      headers: authHeaders,
    });
    if (!response.ok) {
      return parseApiError(response, 'Failed to delete workspace');
    }
    const tenantList = await fetchTenants();
    if (selectedTenantId === tenantId) {
      setSelectedTenantId(tenantList[0]?.tenantId || null);
    }
  }, [authHeaders, fetchTenants, selectedTenantId]);

  const createTenantDomain = useCallback(async (domain: string): Promise<void> => {
    if (!selectedTenantId) {
      throw new Error(t('ctx.errorNoWorkspace'));
    }

    if (!isManageRole(roleForTenant(tenants, selectedTenantId))) {
      throw new Error(t('ctx.errorDomainPermission'));
    }

    const response = await fetch(`${API_BASE}/domains`, {
      method: 'POST',
      headers: authHeaders,
      body: JSON.stringify({
        tenantId: selectedTenantId,
        domain: domain.trim(),
      }),
    });

    if (!response.ok) {
      return parseApiError(response, 'Failed to create domain');
    }

    await refreshTenantDataFor(selectedTenantId, tenants);
  }, [authHeaders, refreshTenantDataFor, selectedTenantId, tenants]);

  const refreshDomain = useCallback(async (domainId: string): Promise<void> => {
    if (!selectedTenantId) {
      throw new Error(t('ctx.errorNoWorkspace'));
    }

    if (!isManageRole(roleForTenant(tenants, selectedTenantId))) {
      throw new Error(t('ctx.errorDomainPermission'));
    }

    const response = await fetch(`${API_BASE}/domains/${encodeURIComponent(domainId)}/refresh`, {
      method: 'POST',
      headers: authHeaders,
      body: JSON.stringify({ tenantId: selectedTenantId }),
    });

    if (!response.ok) {
      return parseApiError(response, 'Failed to refresh domain status');
    }

    await refreshTenantDataFor(selectedTenantId, tenants);
  }, [authHeaders, refreshTenantDataFor, selectedTenantId, tenants]);

  const setDefaultDomain = useCallback(async (domainId: string): Promise<void> => {
    if (!selectedTenantId) {
      throw new Error(t('ctx.errorNoWorkspace'));
    }

    if (!isManageRole(roleForTenant(tenants, selectedTenantId))) {
      throw new Error(t('ctx.errorDomainPermission'));
    }

    const response = await fetch(`${API_BASE}/domains/${encodeURIComponent(domainId)}/default`, {
      method: 'POST',
      headers: authHeaders,
      body: JSON.stringify({ tenantId: selectedTenantId }),
    });

    if (!response.ok) {
      return parseApiError(response, 'Failed to set default domain');
    }

    await refreshTenantDataFor(selectedTenantId, tenants);
  }, [authHeaders, refreshTenantDataFor, selectedTenantId, tenants]);

  const deleteDomain = useCallback(async (domainId: string): Promise<void> => {
    if (!selectedTenantId) {
      throw new Error(t('ctx.errorNoWorkspace'));
    }

    if (!isManageRole(roleForTenant(tenants, selectedTenantId))) {
      throw new Error(t('ctx.errorDomainPermission'));
    }

    const response = await fetch(`${API_BASE}/domains/${encodeURIComponent(domainId)}`, {
      method: 'DELETE',
      headers: authHeaders,
      body: JSON.stringify({ tenantId: selectedTenantId }),
    });

    if (!response.ok) {
      return parseApiError(response, 'Failed to unlink domain');
    }

    await refreshTenantDataFor(selectedTenantId, tenants);
  }, [authHeaders, refreshTenantDataFor, selectedTenantId, tenants]);

  const addOrUpdateMember = useCallback(async (email: string, role: MemberAssignableRole): Promise<void> => {
    if (!selectedTenantId) {
      throw new Error(t('ctx.errorNoWorkspace'));
    }

    const normalizedEmail = email.trim().toLowerCase();
    if (!normalizedEmail || !normalizedEmail.includes('@')) {
      throw new Error(t('ctx.errorMemberEmail'));
    }

    const response = await fetch(`${API_BASE}/tenants/${encodeURIComponent(selectedTenantId)}/members`, {
      method: 'POST',
      headers: authHeaders,
      body: JSON.stringify({
        email: normalizedEmail,
        role,
      }),
    });

    if (!response.ok) {
      return parseApiError(response, 'Failed to update member');
    }

    await fetchMembers(selectedTenantId, true);
  }, [authHeaders, fetchMembers, selectedTenantId]);

  const removeMember = useCallback(async (email: string): Promise<void> => {
    if (!selectedTenantId) {
      throw new Error(t('ctx.errorNoWorkspace'));
    }

    const normalizedEmail = email.trim().toLowerCase();
    if (!normalizedEmail) {
      throw new Error(t('ctx.errorMemberEmail'));
    }

    const response = await fetch(
      `${API_BASE}/tenants/${encodeURIComponent(selectedTenantId)}/members/${encodeURIComponent(normalizedEmail)}`,
      { method: 'DELETE', headers: authHeaders },
    );

    if (!response.ok) {
      return parseApiError(response, 'Failed to remove member');
    }

    await fetchMembers(selectedTenantId, true);
  }, [authHeaders, fetchMembers, selectedTenantId]);

  const createLink = useCallback(async (input: CreateLinkInput): Promise<{ urlCode: string; domainId?: string | null; domain?: string | null }> => {
    if (!selectedTenantId) {
      throw new Error(t('ctx.errorNoTenant'));
    }

    if (!isWriteRole(roleForTenant(tenants, selectedTenantId))) {
      throw new Error(t('ctx.errorNoWritePermission'));
    }

    const originalUrl = input.originalUrl.trim();
    const code = input.urlCode?.trim() || '';
    const redirectType = input.redirectType === '302' ? '302' : '301';

    if (!originalUrl) {
      throw new Error(t('ctx.errorInvalidUrl'));
    }

    try {
      new URL(originalUrl);
    } catch {
      throw new Error(t('ctx.errorInvalidOriginalUrl'));
    }

    if (code && !URL_CODE_REGEX.test(code)) {
      throw new Error(t('ctx.errorInvalidCode'));
    }

    const response = await fetch(`${API_BASE}/links`, {
      method: 'POST',
      headers: authHeaders,
      body: JSON.stringify({
        tenantId: selectedTenantId,
        originalUrl,
        urlCode: code || undefined,
        redirectType,
        domainId: input.domainId?.trim() || undefined,
        password: input.password?.trim() || undefined,
        redirectQueryParams: input.redirectQueryParams,
        activeFrom: input.activeFrom,
        activeUntil: input.activeUntil,
      }),
    });

    if (!response.ok) {
      return parseApiError(response, 'Failed to create link');
    }

    const data = await response.json();
    await refreshTenantDataFor(selectedTenantId, tenants);

    return {
      urlCode: String(data.urlCode || code),
      domainId: data.domainId ? String(data.domainId) : null,
      domain: data.domain ? String(data.domain) : null,
    };
  }, [authHeaders, refreshTenantDataFor, selectedTenantId, tenants]);

  const updateLink = useCallback(async (urlCode: string, input: UpdateLinkInput, options?: { domainId?: string | null }): Promise<void> => {
    if (!selectedTenantId) {
      throw new Error(t('ctx.errorNoWorkspace'));
    }

    if (!isWriteRole(roleForTenant(tenants, selectedTenantId))) {
      throw new Error(t('ctx.errorNoWritePermission'));
    }

    const originalUrl = input.originalUrl.trim();
    const redirectType = input.redirectType === '302' ? '302' : '301';

    if (!originalUrl) {
      throw new Error(t('ctx.errorUrlRequired'));
    }

    try {
      new URL(originalUrl);
    } catch {
      throw new Error(t('ctx.errorInvalidOriginalUrl'));
    }

    const query = new URLSearchParams();
    query.set('tenantId', selectedTenantId);
    if (options?.domainId) {
      query.set('domainId', options.domainId);
    }

    const response = await fetch(`${API_BASE}/links/${encodeURIComponent(urlCode)}?${query.toString()}`, {
      method: 'PUT',
      headers: authHeaders,
      body: JSON.stringify({
        originalUrl,
        redirectType,
        password: input.password?.trim() || undefined,
        clearPassword: input.clearPassword === true,
        redirectQueryParams: input.redirectQueryParams,
        clearRedirectQueryParams: input.clearRedirectQueryParams === true,
        activeFrom: input.activeFrom,
        activeUntil: input.activeUntil,
      }),
    });

    if (!response.ok) {
      return parseApiError(response, 'Failed to update link');
    }

    await refreshTenantDataFor(selectedTenantId, tenants);
  }, [authHeaders, refreshTenantDataFor, selectedTenantId, tenants]);

  const deleteLink = useCallback(async (urlCode: string, options?: { domainId?: string | null }): Promise<void> => {
    if (!selectedTenantId) {
      throw new Error(t('ctx.errorNoWorkspace'));
    }

    if (!isWriteRole(roleForTenant(tenants, selectedTenantId))) {
      throw new Error(t('ctx.errorNoWritePermission'));
    }

    const query = new URLSearchParams();
    query.set('tenantId', selectedTenantId);
    if (options?.domainId) {
      query.set('domainId', options.domainId);
    }

    const response = await fetch(`${API_BASE}/links/${encodeURIComponent(urlCode)}?${query.toString()}`, {
      method: 'DELETE',
      headers: authHeaders,
    });

    if (!response.ok) {
      return parseApiError(response, 'Failed to delete link');
    }

    await refreshTenantDataFor(selectedTenantId, tenants);
  }, [authHeaders, refreshTenantDataFor, selectedTenantId, tenants]);

  const markNotificationRead = useCallback(async (notificationId: string): Promise<void> => {
    const response = await fetch(`${API_BASE}/notifications/${encodeURIComponent(notificationId)}/read`, {
      method: 'POST',
      headers: authHeaders,
    });

    if (!response.ok) {
      return parseApiError(response, 'Failed to mark notification as read');
    }

    setNotifications((current) => current.map((item) => {
      if (item.notificationId !== notificationId || item.readAt) {
        return item;
      }
      return { ...item, readAt: new Date().toISOString() };
    }));
    setUnreadNotifications((current) => Math.max(0, current - 1));
  }, [authHeaders]);

  const markAllNotificationsRead = useCallback(async (): Promise<void> => {
    const response = await fetch(`${API_BASE}/notifications/read-all`, {
      method: 'POST',
      headers: authHeaders,
      body: JSON.stringify({
        tenantId: selectedTenantId || undefined,
      }),
    });

    if (!response.ok) {
      return parseApiError(response, 'Failed to mark all notifications as read');
    }

    const now = new Date().toISOString();
    setNotifications((current) => current.map((item) => ({ ...item, readAt: item.readAt || now })));
    setUnreadNotifications(0);
  }, [authHeaders, selectedTenantId]);

  const setDefaultRedirectType = useCallback((value: RedirectType) => {
    const normalized = value === '302' ? '302' : '301';
    setDefaultRedirectTypeState(normalized);
    localStorage.setItem(DEFAULT_REDIRECT_STORAGE_KEY, normalized);
  }, []);

  const setConfirmDelete = useCallback((value: boolean) => {
    setConfirmDeleteState(value);
    localStorage.setItem(CONFIRM_DELETE_STORAGE_KEY, String(value));
  }, []);

  const makeShortUrl = useCallback((code: string, domain?: string | null): string => {
    const normalizedDomain = typeof domain === 'string' ? domain.trim().toLowerCase() : '';
    if (normalizedDomain) {
      return `https://${normalizedDomain}/${code}`;
    }

    return shortBaseUrl ? `${shortBaseUrl}/${code}` : '';
  }, [shortBaseUrl]);

  // ── Platform admin management ──

  const fetchPlatformAdmins = useCallback(async (): Promise<void> => {
    setPlatformAdminsLoading(true);
    try {
      const response = await fetch(`${API_BASE}/platform/admins`, { headers: authHeaders });
      if (!response.ok) {
        return parseApiError(response, 'Failed to load platform admins');
      }
      const data = await response.json();
      setPlatformAdmins(Array.isArray(data.admins) ? data.admins : []);
    } finally {
      setPlatformAdminsLoading(false);
    }
  }, [authHeaders]);

  const addPlatformAdmin = useCallback(async (email: string): Promise<void> => {
    const response = await fetch(`${API_BASE}/platform/admins`, {
      method: 'POST',
      headers: { ...authHeaders, 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    });
    if (!response.ok) {
      return parseApiError(response, 'Failed to add platform admin');
    }
    await fetchPlatformAdmins();
  }, [authHeaders, fetchPlatformAdmins]);

  const removePlatformAdmin = useCallback(async (email: string): Promise<void> => {
    const response = await fetch(`${API_BASE}/platform/admins/${encodeURIComponent(email)}`, {
      method: 'DELETE',
      headers: authHeaders,
    });
    if (!response.ok) {
      return parseApiError(response, 'Failed to remove platform admin');
    }
    await fetchPlatformAdmins();
  }, [authHeaders, fetchPlatformAdmins]);

  const fetchPendingRegistrations = useCallback(async (): Promise<void> => {
    setPendingRegistrationsLoading(true);
    try {
      const response = await fetch(`${API_BASE}/platform/registrations?status=pending`, { headers: authHeaders });
      if (!response.ok) {
        return parseApiError(response, 'Failed to load pending registrations');
      }
      const data = await response.json();
      setPendingRegistrations(Array.isArray(data.registrations) ? data.registrations : []);
    } finally {
      setPendingRegistrationsLoading(false);
    }
  }, [authHeaders]);

  const approveRegistration = useCallback(async (email: string): Promise<void> => {
    const response = await fetch(`${API_BASE}/platform/registrations/${encodeURIComponent(email)}/approve`, {
      method: 'POST',
      headers: authHeaders,
    });
    if (!response.ok) {
      return parseApiError(response, 'Failed to approve registration');
    }
    await fetchPendingRegistrations();
  }, [authHeaders, fetchPendingRegistrations]);

  const rejectRegistration = useCallback(async (email: string, reason?: string): Promise<void> => {
    const response = await fetch(`${API_BASE}/platform/registrations/${encodeURIComponent(email)}/reject`, {
      method: 'POST',
      headers: authHeaders,
      body: JSON.stringify({ reason: reason || '' }),
    });
    if (!response.ok) {
      return parseApiError(response, 'Failed to reject registration');
    }
    await fetchPendingRegistrations();
  }, [authHeaders, fetchPendingRegistrations]);

  const fetchAutoApproveDomains = useCallback(async (): Promise<void> => {
    setAutoApproveDomainsLoading(true);
    try {
      const response = await fetch(`${API_BASE}/platform/auto-approve-domains`, { headers: authHeaders });
      if (!response.ok) {
        return parseApiError(response, 'Failed to load auto-approve domains');
      }
      const data = await response.json();
      setAutoApproveDomains(Array.isArray(data.patterns) ? data.patterns : []);
    } finally {
      setAutoApproveDomainsLoading(false);
    }
  }, [authHeaders]);

  const addAutoApproveDomain = useCallback(async (pattern: string): Promise<void> => {
    const response = await fetch(`${API_BASE}/platform/auto-approve-domains`, {
      method: 'POST',
      headers: authHeaders,
      body: JSON.stringify({ pattern }),
    });
    if (!response.ok) {
      return parseApiError(response, 'Failed to add auto-approve domain');
    }
    await fetchAutoApproveDomains();
  }, [authHeaders, fetchAutoApproveDomains]);

  const removeAutoApproveDomain = useCallback(async (pattern: string): Promise<void> => {
    const response = await fetch(`${API_BASE}/platform/auto-approve-domains/${encodeURIComponent(pattern)}`, {
      method: 'DELETE',
      headers: authHeaders,
    });
    if (!response.ok) {
      return parseApiError(response, 'Failed to remove auto-approve domain');
    }
    await fetchAutoApproveDomains();
  }, [authHeaders, fetchAutoApproveDomains]);

  const fetchServiceAccounts = useCallback(async (): Promise<void> => {
    setServiceAccountsLoading(true);
    try {
      const response = await fetch(`${API_BASE}/platform/service-accounts`, { headers: authHeaders });
      if (!response.ok) {
        return parseApiError(response, 'Failed to load service accounts');
      }
      const data = await response.json();
      setServiceAccounts(Array.isArray(data.serviceAccounts) ? data.serviceAccounts : []);
    } finally {
      setServiceAccountsLoading(false);
    }
  }, [authHeaders]);

  const createServiceAccount = useCallback(async (input: {
    name: string;
    actAsUserId: string;
    scopes: ServiceAccountScope[];
    expiresAt?: string;
  }): Promise<{ apiKey: string; serviceAccount: PlatformServiceAccount }> => {
    const response = await fetch(`${API_BASE}/platform/service-accounts`, {
      method: 'POST',
      headers: authHeaders,
      body: JSON.stringify(input),
    });
    if (!response.ok) {
      return parseApiError(response, 'Failed to create service account');
    }
    const data = await response.json();
    await fetchServiceAccounts();
    return {
      apiKey: String(data.apiKey || ''),
      serviceAccount: data.serviceAccount as PlatformServiceAccount,
    };
  }, [authHeaders, fetchServiceAccounts]);

  const rotateServiceAccountKey = useCallback(async (serviceAccountId: string): Promise<{ apiKey: string }> => {
    const response = await fetch(`${API_BASE}/platform/service-accounts/${encodeURIComponent(serviceAccountId)}/rotate-key`, {
      method: 'POST',
      headers: authHeaders,
    });
    if (!response.ok) {
      return parseApiError(response, 'Failed to rotate service account API key');
    }
    const data = await response.json();
    await fetchServiceAccounts();
    return { apiKey: String(data.apiKey || '') };
  }, [authHeaders, fetchServiceAccounts]);

  const revokeServiceAccount = useCallback(async (serviceAccountId: string): Promise<void> => {
    const response = await fetch(`${API_BASE}/platform/service-accounts/${encodeURIComponent(serviceAccountId)}`, {
      method: 'DELETE',
      headers: authHeaders,
    });
    if (!response.ok) {
      return parseApiError(response, 'Failed to revoke service account');
    }
    await fetchServiceAccounts();
  }, [authHeaders, fetchServiceAccounts]);

  // ── Platform domain management ──

  const fetchPlatformDomains = useCallback(async (): Promise<void> => {
    setPlatformDomainsLoading(true);
    try {
      const response = await fetch(`${API_BASE}/platform/domains`, { headers: authHeaders });
      if (!response.ok) {
        return parseApiError(response, 'Failed to load platform domains');
      }
      const data = await response.json();
      setPlatformDomains(Array.isArray(data.domains) ? data.domains : []);
    } finally {
      setPlatformDomainsLoading(false);
    }
  }, [authHeaders]);

  const createPlatformDomain = useCallback(async (domain: string): Promise<void> => {
    const response = await fetch(`${API_BASE}/platform/domains`, {
      method: 'POST',
      headers: authHeaders,
      body: JSON.stringify({ domain }),
    });
    if (!response.ok) {
      return parseApiError(response, 'Failed to create platform domain');
    }
    await fetchPlatformDomains();
  }, [authHeaders, fetchPlatformDomains]);

  const linkDomainToAllWorkspaces = useCallback(async (domainId: string): Promise<{ linked: number; defaultSet: number; skipped: number }> => {
    const response = await fetch(`${API_BASE}/platform/domains/${encodeURIComponent(domainId)}/link-all-workspaces`, {
      method: 'POST',
      headers: authHeaders,
    });
    if (!response.ok) {
      return parseApiError(response, 'Failed to link domain to all workspaces');
    }
    const data = await response.json();
    await fetchPlatformDomains();
    return { linked: data.linked ?? 0, defaultSet: data.defaultSet ?? 0, skipped: data.skipped ?? 0 };
  }, [authHeaders, fetchPlatformDomains]);

  const linkDomainToWorkspace = useCallback(async (domainId: string, tenantId: string): Promise<void> => {
    const response = await fetch(`${API_BASE}/platform/domains/${encodeURIComponent(domainId)}/workspaces`, {
      method: 'POST',
      headers: authHeaders,
      body: JSON.stringify({ tenantId }),
    });
    if (!response.ok) {
      return parseApiError(response, 'Failed to link domain to workspace');
    }
    await fetchPlatformDomains();
  }, [authHeaders, fetchPlatformDomains]);

  const unlinkDomainFromWorkspace = useCallback(async (domainId: string, tenantId: string): Promise<void> => {
    const response = await fetch(
      `${API_BASE}/platform/domains/${encodeURIComponent(domainId)}/workspaces/${encodeURIComponent(tenantId)}`,
      { method: 'DELETE', headers: authHeaders },
    );
    if (!response.ok) {
      return parseApiError(response, 'Failed to unlink domain from workspace');
    }
    await fetchPlatformDomains();
  }, [authHeaders, fetchPlatformDomains]);

  const refreshPlatformDomain = useCallback(async (domainId: string): Promise<void> => {
    const response = await fetch(`${API_BASE}/platform/domains/${encodeURIComponent(domainId)}/refresh`, {
      method: 'POST',
      headers: authHeaders,
    });
    if (!response.ok) {
      return parseApiError(response, 'Failed to refresh platform domain');
    }
    await fetchPlatformDomains();
  }, [authHeaders, fetchPlatformDomains]);

  const deletePlatformDomain = useCallback(async (domainId: string): Promise<{ distributionId: string | null; note: string | null }> => {
    const response = await fetch(`${API_BASE}/platform/domains/${encodeURIComponent(domainId)}`, {
      method: 'DELETE',
      headers: authHeaders,
    });
    if (!response.ok) {
      return parseApiError(response, 'Failed to delete platform domain');
    }
    const data = await response.json();
    await fetchPlatformDomains();
    return {
      distributionId: data.distributionId ?? null,
      note: data.note ?? null,
    };
  }, [authHeaders, fetchPlatformDomains]);

  const getNotFoundPageConfig = useCallback(async (tenantId: string): Promise<NotFoundPageConfig> => {
    const response = await fetch(`${API_BASE}/tenants/${encodeURIComponent(tenantId)}/not-found-page`, {
      headers: authHeaders,
    });
    if (!response.ok) {
      return parseApiError(response, 'Failed to fetch Not Found page settings');
    }
    const data = await response.json();
    return data.config as NotFoundPageConfig;
  }, [authHeaders]);

  const updateNotFoundPageConfig = useCallback(async (
    tenantId: string,
    config: Omit<NotFoundPageConfig, 'updatedAt' | 'updatedBy'>,
  ): Promise<NotFoundPageConfig> => {
    const response = await fetch(`${API_BASE}/tenants/${encodeURIComponent(tenantId)}/not-found-page`, {
      method: 'PUT',
      headers: authHeaders,
      body: JSON.stringify({ config }),
    });
    if (!response.ok) {
      return parseApiError(response, 'Failed to update Not Found page settings');
    }
    const data = await response.json();
    return data.config as NotFoundPageConfig;
  }, [authHeaders]);

  const value = useMemo<AdminContextValue>(() => {
    return {
      loading,
      notice,
      isPlatformAdmin,
      tenants,
      selectedTenantId,
      selectedTenant,
      domains,
      notifications,
      unreadNotifications,
      notificationsLoading,
      analytics,
      analyticsQuery,
      members,
      shortBaseUrl,
      canWriteLinks,
      canManageMembers,
      preferences: {
        confirmDelete,
        defaultRedirectType,
      },
      setNotice,
      setSelectedTenantId,
      refreshTenants,
      refreshTenantData,
      refreshAnalytics,
      refreshNotifications,
      createTenant,
      renameTenant,
      deleteTenant,
      createTenantDomain,
      refreshDomain,
      setDefaultDomain,
      deleteDomain,
      addOrUpdateMember,
      removeMember,
      createLink,
      updateLink,
      deleteLink,
      setDefaultRedirectType,
      setConfirmDelete,
      markNotificationRead,
      markAllNotificationsRead,
      makeShortUrl,
      platformAdmins,
      platformAdminsLoading,
      fetchPlatformAdmins,
      addPlatformAdmin,
      removePlatformAdmin,
      pendingRegistrations,
      pendingRegistrationsLoading,
      fetchPendingRegistrations,
      approveRegistration,
      rejectRegistration,
      autoApproveDomains,
      autoApproveDomainsLoading,
      fetchAutoApproveDomains,
      addAutoApproveDomain,
      removeAutoApproveDomain,
      serviceAccounts,
      serviceAccountsLoading,
      fetchServiceAccounts,
      createServiceAccount,
      rotateServiceAccountKey,
      revokeServiceAccount,
      platformDomains,
      platformDomainsLoading,
      platformDefaultDomain,
      fetchPlatformDomains,
      createPlatformDomain,
      linkDomainToAllWorkspaces,
      linkDomainToWorkspace,
      unlinkDomainFromWorkspace,
      refreshPlatformDomain,
      deletePlatformDomain,
      getNotFoundPageConfig,
      updateNotFoundPageConfig,
    };
  }, [
    addOrUpdateMember,
    analytics,
    analyticsQuery,
    approveRegistration,
    autoApproveDomains,
    autoApproveDomainsLoading,
    addAutoApproveDomain,
    createServiceAccount,
    canManageMembers,
    canWriteLinks,
    confirmDelete,
    createLink,
    createPlatformDomain,
    createTenantDomain,
    createTenant,
    deleteDomain,
    deletePlatformDomain,
    deleteTenant,
    defaultRedirectType,
    deleteLink,
    domains,
    fetchPlatformAdmins,
    fetchAutoApproveDomains,
    fetchPendingRegistrations,
    fetchPlatformDomains,
    fetchServiceAccounts,
    getNotFoundPageConfig,
    isPlatformAdmin,
    linkDomainToAllWorkspaces,
    linkDomainToWorkspace,
    markAllNotificationsRead,
    markNotificationRead,
    loading,
    makeShortUrl,
    members,
    notice,
    notifications,
    notificationsLoading,
    platformAdmins,
    platformAdminsLoading,
    pendingRegistrations,
    pendingRegistrationsLoading,
    platformDefaultDomain,
    platformDomains,
    platformDomainsLoading,
    refreshTenantData,
    refreshAnalytics,
    refreshNotifications,
    refreshDomain,
    refreshPlatformDomain,
    refreshTenants,
    removeMember,
    removeAutoApproveDomain,
    revokeServiceAccount,
    rotateServiceAccountKey,
    renameTenant,
    rejectRegistration,
    selectedTenant,
    selectedTenantId,
    serviceAccounts,
    serviceAccountsLoading,
    setConfirmDelete,
    setDefaultRedirectType,
    setDefaultDomain,
    shortBaseUrl,
    tenants,
    unreadNotifications,
    unlinkDomainFromWorkspace,
    updateNotFoundPageConfig,
    updateLink,
  ]);

  return <AdminContext.Provider value={value}>{children}</AdminContext.Provider>;
}

export function useAdmin() {
  const context = useContext(AdminContext);
  if (!context) {
    throw new Error('useAdmin must be used within AdminProvider');
  }

  return context;
}
